import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';

import dayjs from '@api/services/tools/dayjs';
import { parseUrlParams, setAtEndOfDay } from '@api/services/tools/handleUrlParams';
import { GetEventsCounts } from '@api/store/logs/actions';
import { LogTypesString } from '@api/store/logs/types';

import { EventsFiltersTypeEnum } from '../Interface';
import { useDefaultLogFilters } from './useDefaultLogFilters';
import { safelyDecodeBase64Url } from '@dev/ee-tools';

export const useEventsFilters = ({ eventFiltersType }: Record<string, any>): any => {
    const isInProgressLogs = eventFiltersType === EventsFiltersTypeEnum.InProgress;
    const location = useLocation();
    const dispatch = useDispatch();

    /**should not fetch data on inprogress screen */
    const [shouldRefetchData, setShouldRefechData] = useState(!isInProgressLogs);
    const { logType = LogTypesString.EMAIL, channelName } = useParams<{ logType: LogTypesString; channelName: string }>();
    const query = parseUrlParams(location.search);
    const defaultLogFilters = useDefaultLogFilters(isInProgressLogs);
    const decodedChannelName = channelName && safelyDecodeBase64Url(channelName);

    useEffect(() => {
        !isInProgressLogs && setShouldRefechData(true);
    }, [query.email, query.from, query.to, query.channel, query.campaign, logType, query.search]);

    const fetchEventsCounts = () => {
        if (!shouldRefetchData || isInProgressLogs) {
            return null;
        }

        const dateParam = {
            ...defaultLogFilters,
            ...(query?.from &&
                query?.to && {
                    from: dayjs.utc(query?.from as string).toJSON(),
                    to: setAtEndOfDay(dayjs.utc(query?.to as string).toJSON()),
                }),
        };
        const params = {
            ...dateParam,
            email: query.search as string,
            eventType: logType,
            ...(logType?.toLowerCase?.() !== LogTypesString.NOTIFICATION && {
                channelName: (query.channel as string) || (query.campaign as string) || decodedChannelName,
                includeSms: logType?.toLowerCase?.() === LogTypesString.SMS?.toLowerCase?.(),
                includeEmail: logType?.toLowerCase?.() === LogTypesString.EMAIL?.toLowerCase?.(),
            }),
            onlyLastEvent: isInProgressLogs,
        };

        dispatch(GetEventsCounts(params));
        setShouldRefechData(false);
    };

    return { fetchEventsCounts, shouldRefetchData };
};
